import React from 'react';
import { Link } from 'react-router-dom';
import { FaTiktok } from 'react-icons/fa';
import { FiUpload, FiSend, FiScissors, FiType, FiCrosshair, FiTrendingUp, FiCpu, FiFilm, FiChevronRight } from 'react-icons/fi';
import './landing-v2.css';

import aiCuration from './../../assets/images/AI-Curation.png';
import autoCaption from './../../assets/images/Auto-Caption.png';
import faceTracking from './../../assets/images/Face-Tracking.png';
import viralityScore from './../../assets/images/Virality-Score.png';
import anim1 from './../../assets/images/animation1.gif';
import anim2 from './../../assets/images/animation2.gif';
import anim3 from './../../assets/images/animation3.gif';

const scrollToTool = () => window.scrollTo({ top: 0, behavior: 'smooth' });

const FEATURES = [
    { img: aiCuration, icon: <FiScissors />, title: 'AI Curation', desc: 'AI analyzes your video and compiles the highlights into potential viral shorts.' },
    { img: autoCaption, icon: <FiType />, title: 'Auto Captions', desc: 'Automatic, styled subtitles and emojis that boost watch-time on every platform.' },
    { img: faceTracking, icon: <FiCrosshair />, title: 'Face Tracking', desc: 'Keeps the speaker perfectly framed and centered in vertical 9:16 video.' },
    { img: viralityScore, icon: <FiTrendingUp />, title: 'Virality Score', desc: 'Each short gets a score based on analysis of thousands of popular videos.' },
];

const FLOW = [
    { icon: <FiUpload />, title: 'Upload', desc: 'Drop in a long-form video — a podcast, interview, or talk.' },
    { icon: <FiCpu />, title: 'AI processing', desc: 'We transcribe it and find the most engaging moments.' },
    { icon: <FiFilm />, title: 'Pick your shorts', desc: 'Browse the generated clips with captions and virality scores.' },
    { icon: <FaTiktok />, title: 'Connect', desc: 'Securely link your TikTok, YouTube, or Instagram account.' },
    { icon: <FiSend />, title: 'Post', desc: 'Publish your short to your social accounts in one click.' },
];

const FAQS = [
    { q: 'How does Shorts Creator pick the best parts of my video?', a: "Our AI analyzes your video's content and engagement signals to identify the segments most likely to resonate with your audience." },
    { q: 'How do I post my shorts to TikTok?', a: 'Once your shorts are generated, connect your TikTok account on the Connect Accounts screen. You can then publish a clip to TikTok directly.' },
    { q: 'Which platforms can I publish to?', a: 'You can publish your finished shorts to TikTok, YouTube Shorts, and Instagram Reels from a single place.' },
    { q: 'How long does it take to get my shorts?', a: 'Shorts are typically generated within about 30 minutes of submitting your video.' },
];

const LandingMarketingV2 = () => {
    return (
        <div className="lp2">
            {/* ---------- FEATURES ---------- */}
            <section className="lp2-section">
                <div className="lp2-container">
                    <div className="lp2-head">
                        <span className="lp2-eyebrow">The power of AI</span>
                        <h2>Everything you need to go viral</h2>
                        <p>One tool that clips, captions, reframes, and scores your video — then posts it for you.</p>
                    </div>
                    <div className="lp2-grid-4">
                        {FEATURES.map((f) => (
                            <div className="lp2-card" key={f.title}>
                                <div className="lp2-card-img"><img src={f.img} alt={f.title} /></div>
                                <div className="lp2-card-body">
                                    <div className="lp2-card-ic">{f.icon}</div>
                                    <h3>{f.title}</h3>
                                    <p>{f.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- HOW IT WORKS (full flow diagram) ---------- */}
            <section className="lp2-section lp2-soft">
                <div className="lp2-container">
                    <div className="lp2-head">
                        <span className="lp2-eyebrow">How it works</span>
                        <h2>From long-form video to social media — the whole flow</h2>
                        <p>One place to clip, review, connect, and post — no editing skills needed.</p>
                    </div>
                    <div className="lp2-flow">
                        {FLOW.map((s, i) => (
                            <React.Fragment key={s.title}>
                                <div className="lp2-flow-node">
                                    <div className="lp2-flow-ic">{s.icon}</div>
                                    <span className="lp2-flow-step">Step {i + 1}</span>
                                    <h3>{s.title}</h3>
                                    <p>{s.desc}</p>
                                </div>
                                {i < FLOW.length - 1 && (
                                    <div className="lp2-flow-arrow"><FiChevronRight /></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="lp2-flow-cta">
                        <button className="lp2-btn lp2-btn-primary" onClick={scrollToTool}>Create a short — free</button>
                    </div>
                </div>
            </section>

            {/* ---------- SHOWCASE ---------- */}
            <section className="lp2-section">
                <div className="lp2-container">
                    <div className="lp2-head">
                        <span className="lp2-eyebrow">Made with Shorts Creator</span>
                        <h2>Shorts your audience will actually watch</h2>
                        <p>Auto-captioned, face-tracked, and formatted for TikTok, Reels, and Shorts.</p>
                    </div>
                    <div className="lp2-showcase">
                        {[anim1, anim2, anim3].map((g, i) => (
                            <div className="lp2-show-phone" key={i}><img src={g} alt={`Sample short ${i + 1}`} /></div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- FAQ ---------- */}
            <section className="lp2-section lp2-soft">
                <div className="lp2-container">
                    <div className="lp2-head">
                        <span className="lp2-eyebrow">FAQ</span>
                        <h2>Frequently asked questions</h2>
                    </div>
                    <div className="lp2-faq">
                        {FAQS.map((item, i) => (
                            <details key={i} open={i === 0}>
                                <summary>{item.q}</summary>
                                <p>{item.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- FINAL CTA ---------- */}
            <section className="lp2-section">
                <div className="lp2-cta-band">
                    <h2>Ready to grow on TikTok?</h2>
                    <p>Create your first AI-powered short and publish it in minutes — no editing skills required.</p>
                    <div className="lp2-hero-cta" style={{ justifyContent: 'center' }}>
                        <button className="lp2-btn lp2-btn-light" onClick={scrollToTool}>Create a short — free</button>
                        <Link to="/upload" className="lp2-btn lp2-btn-ghost">Connect accounts</Link>
                    </div>
                    <div className="lp2-contact">
                        <a href="mailto:minimalee3950@gmail.com" style={{ color: '#dbe4f5', fontSize: '14px', display: 'inline-block', marginTop: '20px' }}>
                            Questions? Contact us at minimalee3950@gmail.com
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LandingMarketingV2;
